import React, { useState } from 'react';
import { X } from 'lucide-react';

const C = {
  primary: 'var(--c-primary)', secondary: 'var(--c-secondary)', bg: 'var(--c-bg)',
  surface: 'var(--c-surface)', border: 'var(--c-border)', text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)', ok: 'var(--c-ok)', warn: 'var(--c-warn)', danger: 'var(--c-danger)',
};

const HW_HEADERS: Record<string, string> = {
  assettag: 'assetTag', name: 'name', assettype: 'assetType', serialnumber: 'serialNumber',
  status: 'status', warrantyenddate: 'warrantyEndDate', purchasedat: 'purchasedAt',
  assigneduseremail: 'assignedUserEmail', notes: 'notes',
};

type HwRow = {
  assetTag: string; name: string; assetType: string; serialNumber?: string | null;
  status?: string | null; warrantyEndDate?: string | null; purchasedAt?: string | null;
  assignedUserEmail?: string | null; notes?: string | null;
};

type HwDup = {
  assetTag: string; existingId: number;
  existing: { name: string; assetType: string; status: string };
  csv: {
    name: string; assetType: string; serialNumber: string | null; warrantyEndDate: string | null;
    purchasedAt: string | null; status: string; assignedUserId: number | null; notes: string | null;
  };
};

type ImportResult = {
  imported: number; duplicates: HwDup[];
  errors: { assetTag: string; reason: string }[]; unmatchedEmails: string[];
};

// Minimal CSV reader: handles quoted fields and embedded commas/newlines.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '', row: string[] = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) { if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQ = false; } else field += ch; }
    else if (ch === '"') inQ = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (ch !== '\r') field += ch;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

function parseHardwareCsv(text: string): HwRow[] {
  const rows = parseCsv(text);
  if (!rows.length) return [];
  const header = rows[0].map(h => h.trim().toLowerCase().replace(/\s+/g, ''));
  const idx: Record<string, number> = {};
  header.forEach((h, i) => { const k = HW_HEADERS[h]; if (k) idx[k] = i; });
  const out: HwRow[] = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    if (!cells.length || cells.every(c => !c.trim())) continue;
    const get = (k: string) => (idx[k] !== undefined ? (cells[idx[k]] ?? '').trim() : '');
    const assetTag = get('assetTag'), name = get('name'), assetType = get('assetType');
    if (!assetTag && !name && !assetType) continue;
    out.push({
      assetTag, name, assetType,
      serialNumber: get('serialNumber') || null, status: get('status') || null,
      warrantyEndDate: get('warrantyEndDate') || null, purchasedAt: get('purchasedAt') || null,
      assignedUserEmail: get('assignedUserEmail') || null, notes: get('notes') || null,
    });
  }
  return out;
}

const EXAMPLE = [
  'AssetTag,Name,AssetType,SerialNumber,Status,WarrantyEndDate,PurchasedAt,AssignedUserEmail,Notes',
  'LAP-001,Dell Latitude 7440,laptop,SN12345,in-use,2027-06-01,2024-06-01,jane.doe@societybrands.com,Finance laptop',
  'MON-014,Dell 27-inch Monitor,monitor,SNMON014,spare,,2023-11-15,,Spare in storage',
  'DCK-003,WD19S Dock,dock,SNDCK003,in-use,2026-01-01,2023-05-20,,',
].join('\n') + '\n';

export const AssetImportPanel: React.FC<{ api: any; onClose: () => void; onChanged: () => void }> = ({ api, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dups, setDups] = useState<HwDup[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  const downloadExample = () => {
    const blob = new Blob([EXAMPLE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'hardware-assets-example.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onFile = async (file: File | null) => {
    if (!file) return;
    setBusy(true); setMsg(null); setResult(null); setDups([]);
    try {
      const rows = parseHardwareCsv(await file.text());
      if (!rows.length) { setMsg('No usable rows found — the first line must be a header with at least AssetTag, Name, AssetType.'); return; }
      const res = await api.post('/api/assets/import', { items: rows }) as ImportResult;
      setResult(res); setDups(res.duplicates ?? []);
      onChanged();
    } catch (e: any) { setMsg(`Import failed: ${e?.message ?? 'error'}`); }
    finally { setBusy(false); }
  };

  const resolve = async (d: HwDup, action: 'update' | 'skip') => {
    if (action === 'update') {
      try {
        await api.patch(`/api/assets/${d.existingId}`, {
          name: d.csv.name, assetType: d.csv.assetType, serialNumber: d.csv.serialNumber,
          warrantyEndDate: d.csv.warrantyEndDate, purchasedAt: d.csv.purchasedAt,
          status: d.csv.status, assignedUserId: d.csv.assignedUserId, notes: d.csv.notes,
        });
        onChanged();
      } catch (e: any) { alert(`Update failed: ${e?.message ?? 'error'}`); return; }
    }
    setDups(prev => prev.filter(x => x.existingId !== d.existingId));
  };

  const btn = (bg: string, color: string): React.CSSProperties => ({
    background: bg, color, border: bg === 'transparent' ? `1px solid ${C.border}` : 'none',
    borderRadius: 4, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  });

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: C.text }}>Import hardware from CSV</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, lineHeight: 0 }} aria-label="Close">
          <X size={18} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ ...btn(C.primary, '#fff'), opacity: busy ? 0.6 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy ? 'Importing…' : 'Choose CSV file'}
          <input type="file" accept=".csv,text/csv" disabled={busy} style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0] ?? null; e.currentTarget.value = ''; onFile(f); }} />
        </label>
        <button onClick={downloadExample} style={btn('transparent', C.primary)}>Download example CSV</button>
      </div>

      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: result || msg ? 12 : 0 }}>
        Columns: <strong>AssetTag</strong>, <strong>Name</strong>, <strong>AssetType</strong> (laptop/monitor/phone/dock/other) are required.
        Optional: SerialNumber, Status (in-use/spare/repair/retired), WarrantyEndDate (YYYY-MM-DD), PurchasedAt, AssignedUserEmail, Notes.
      </div>

      {msg && (
        <div style={{ fontSize: 13, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 14px' }}>{msg}</div>
      )}

      {result && (
        <div style={{ fontSize: 13, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 14px', marginBottom: dups.length || result.errors.length || result.unmatchedEmails.length ? 12 : 0 }}>
          Imported <strong>{result.imported}</strong> new asset{result.imported === 1 ? '' : 's'}.
          {dups.length ? ` ${dups.length} existing tag${dups.length === 1 ? '' : 's'} need review.` : ''}
          {result.unmatchedEmails.length > 0 && (
            <div style={{ marginTop: 6, color: C.warn }}>
              Imported but left unassigned — these emails didn’t match a user: {result.unmatchedEmails.join(', ')}
            </div>
          )}
          {result.errors.length > 0 && (
            <div style={{ marginTop: 6, color: C.danger }}>
              Skipped {result.errors.length} row{result.errors.length === 1 ? '' : 's'}: {result.errors.map(e => `${e.assetTag || '(no tag)'} — ${e.reason}`).join('; ')}
            </div>
          )}
        </div>
      )}

      {dups.length > 0 && (
        <div style={{ border: `1px solid ${C.warn}`, borderRadius: 8, padding: 14, background: `${C.warn}10` }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>Review duplicates ({dups.length})</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>
            These asset tags already exist (tags are unique). “Update” overwrites the existing asset with your CSV values; “Skip” leaves it unchanged.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dups.map(d => (
              <div key={d.existingId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, fontSize: 13, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 12px' }}>
                <span>
                  <strong>{d.assetTag}</strong>{' '}
                  <span style={{ color: C.textMuted }}>
                    — currently “{d.existing.name}” ({d.existing.assetType}, {d.existing.status}) → CSV: “{d.csv.name}” ({d.csv.assetType}, {d.csv.status})
                  </span>
                </span>
                <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => resolve(d, 'update')} style={btn(C.primary, '#fff')}>Update</button>
                  <button onClick={() => resolve(d, 'skip')} style={btn('transparent', C.textMuted)}>Skip</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
